/* global YT, Vimeo */

if (!customElements.get('custom-video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }

      const section = this.closest('.video-section');
      const toggleBtn = section.querySelector('[data-video-toggle]');
      
      if (toggleBtn) {
        toggleBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevents the section click listener from firing
          this.toggle();
        });

        // Update button text when video state changes
        this.addEventListener('video-state-change', (e) => {
          const toggleBtn = this.closest('.video-section').querySelector('[data-video-toggle]');
          if (!toggleBtn) return;

          const iconPause = toggleBtn.querySelector(".icon-pause");
          const iconPlay = toggleBtn.querySelector(".icon-play");
          if (e.detail.paused) {
            iconPause.style.display = 'none';
            iconPlay.style.display = 'block';
          } else {
            iconPause.style.display = 'block';
            iconPlay.style.display = 'none';
          }
        });
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.desktopVideo = this.querySelector('.video-desktop video');
      this.mobileVideo = this.querySelector('.video-mobile video');
    
      const setActivePlayer = () => {
        const isMobile = window.innerWidth <= 768;
        const newPlayer = isMobile && this.mobileVideo ? this.mobileVideo : this.desktopVideo;
      
        if (newPlayer === this.player) return;
      
        // Pause the old player if switching
        if (this.player) {
          this.player.pause();
          this.player.removeEventListener('pause', this._onPause);
          this.player.removeEventListener('play', this._onPlay);
        }
      
        this.player = newPlayer;
        if (!this.player) return;
      
        this.player.muted = true;
        this.player.playsInline = true;
        this.player.setAttribute('playsinline', '');
        this.player.autoplay = true;
      
        this._onPause = () => { if (this.inViewport) this.pausedByUser = true; };
        this._onPlay = () => { this.closest('.video-section').classList.add('video-section--played'); };
      
        this.player.addEventListener('pause', this._onPause);
        this.player.addEventListener('play', this._onPlay);
      
        this.addObserver(this.player);
        this.play();
      };
    
      setActivePlayer();
      window.addEventListener('resize', window.debounce(() => setActivePlayer(), 200));
    
      const deferredMedia = this.closest('deferred-media');
      if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
        if (this.player) this.player.muted = true;
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;
    
      if (this.type === 'youtube') {
        this.player.playVideo();
      } else if (this.type === 'vimeo') {
        this.player.play();
      } else {
        // Safari returns a Promise — must handle it
        const playPromise = this.player.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay was prevented — ensure muted and retry once
            this.player.muted = true;
            this.player.play().catch(() => {});
          });
        }
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
       const pausePromise = this.player.pause();
       if (pausePromise !== undefined) {
         pausePromise.catch(() => {});
       }
       this.pausedByUser = false;
      }
    }

    toggle() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        const state = this.player.getPlayerState();
        if (state === 1) { // 1 is playing
          this.player.pauseVideo();
          this.pausedByUser = true;
        } else {
          this.pausedByUser = false;
          this.player.playVideo();
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (paused) {
            this.pausedByUser = false;
            this.player.play();
          } else {
            this.player.pause();
            this.pausedByUser = true;
          }
        });
      } else {
        // Standard Video Tag
        if (this.player.paused) {
          this.pausedByUser = false;
          this.player.play();
        } else {
          this.player.pause();
          this.pausedByUser = true;
        }
      }
      // Trigger a custom event so the button can update its text
      this.dispatchEvent(new CustomEvent('video-state-change', { 
        detail: { paused: this.pausedByUser } 
      }));
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      observer.observe(el);
    }
  }

  customElements.define('custom-video-component', Video);
}
